import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { CometState, GAME_CONSTANTS } from '@/types/game';

interface CometProps {
  comets: CometState[];
}

interface TrailParticle {
  position: THREE.Vector3;
  color: THREE.Color;
  createdAt: number;
  opacity: number;
}

export const Comet = ({ comets }: CometProps) => {
  const groupRef = useRef<THREE.Group>(null);
  const trailsRef = useRef<Map<string, TrailParticle[]>>(new Map());
  const timeRef = useRef(0);
  const lastTrailEmit = useRef<Map<string, number>>(new Map());

  // Trail particle geometry
  const trailGeometry = useMemo(() => new THREE.SphereGeometry(8, 8, 8), []);
  const cometGeometry = useMemo(() => new THREE.SphereGeometry(1, 32, 32), []);
  
  // Create instanced mesh for trail particles
  const maxTrailParticles = GAME_CONSTANTS.COMET_TRAIL_LENGTH * GAME_CONSTANTS.COMET_MAX_COUNT;
  const trailMeshRef = useRef<THREE.InstancedMesh>(null);
  const tempObject = useMemo(() => new THREE.Object3D(), []);
  const tempColor = useMemo(() => new THREE.Color(), []);

  useFrame((state, delta) => {
    if (!groupRef.current) return;
    
    timeRef.current += delta * 1000;
    const now = timeRef.current;
    
    // Update each comet
    comets.forEach((comet, cometIndex) => {
      // Calculate rainbow color based on time
      const hue = ((now + cometIndex * 500) / GAME_CONSTANTS.COMET_COLOR_CYCLE_SPEED) % 1;
      const currentColor = new THREE.Color().setHSL(hue, 1.0, 0.5);
      
      // Get or create trail for this comet
      if (!trailsRef.current.has(comet.id)) {
        trailsRef.current.set(comet.id, []);
        lastTrailEmit.current.set(comet.id, 0);
      }
      
      const trail = trailsRef.current.get(comet.id)!;
      const lastEmit = lastTrailEmit.current.get(comet.id) || 0;
      
      // Emit trail particles every 50ms
      if (now - lastEmit > 50) {
        trail.push({
          position: comet.position.clone(),
          color: currentColor.clone(),
          createdAt: now,
          opacity: 1,
        });
        
        // Limit trail length
        if (trail.length > GAME_CONSTANTS.COMET_TRAIL_LENGTH) {
          trail.shift();
        }
        
        lastTrailEmit.current.set(comet.id, now);
      }
      
      // Update trail particle opacities
      trail.forEach((particle) => {
        const age = now - particle.createdAt;
        const fadeTime = 2500; // 2.5 second fade
        particle.opacity = Math.max(0, 1 - age / fadeTime);
      });
      
      // Remove fully faded particles
      trailsRef.current.set(
        comet.id,
        trail.filter((p) => p.opacity > 0)
      );
    });
    
    // Clean up trails for removed comets
    const cometIds = new Set(comets.map((c) => c.id));
    trailsRef.current.forEach((_, id) => {
      if (!cometIds.has(id)) {
        trailsRef.current.delete(id);
        lastTrailEmit.current.delete(id);
      }
    });
    
    // Update trail instanced mesh
    if (trailMeshRef.current) {
      let particleIndex = 0;
      
      trailsRef.current.forEach((trail) => {
        trail.forEach((particle) => {
          if (particleIndex >= maxTrailParticles) return;
          
          tempObject.position.copy(particle.position);
          tempObject.scale.setScalar(particle.opacity * 0.5);
          tempObject.updateMatrix();
          
          trailMeshRef.current!.setMatrixAt(particleIndex, tempObject.matrix);
          
          // Set color with alpha
          tempColor.copy(particle.color);
          trailMeshRef.current!.setColorAt(particleIndex, tempColor);
          
          particleIndex++;
        });
      });
      
      // Hide unused instances
      for (let i = particleIndex; i < maxTrailParticles; i++) {
        tempObject.position.set(0, 0, 10000); // Move far away
        tempObject.scale.setScalar(0);
        tempObject.updateMatrix();
        trailMeshRef.current.setMatrixAt(i, tempObject.matrix);
      }
      
      trailMeshRef.current.instanceMatrix.needsUpdate = true;
      if (trailMeshRef.current.instanceColor) {
        trailMeshRef.current.instanceColor.needsUpdate = true;
      }
    }
  });

  // Calculate current hue for each comet head
  const getCometColor = (cometIndex: number) => {
    const hue = ((timeRef.current + cometIndex * 500) / GAME_CONSTANTS.COMET_COLOR_CYCLE_SPEED) % 1;
    return new THREE.Color().setHSL(hue, 1.0, 0.5);
  };

  return (
    <group ref={groupRef}>
      {/* Trail particles - instanced mesh for performance */}
      <instancedMesh
        ref={trailMeshRef}
        args={[trailGeometry, undefined, maxTrailParticles]}
        frustumCulled={false}
      >
        <meshStandardMaterial
          transparent
          opacity={0.7}
          emissive="white"
          emissiveIntensity={0.8}
          vertexColors
        />
      </instancedMesh>
      
      {/* Comet heads - individual meshes for distinct rainbow effect */}
      {comets.map((comet, index) => (
        <CometHead key={comet.id} comet={comet} index={index} />
      ))}
    </group>
  );
};

// Individual comet head with rainbow shimmer
const CometHead = ({ comet, index }: { comet: CometState; index: number }) => {
  const meshRef = useRef<THREE.Mesh>(null);
  const glowRef = useRef<THREE.Mesh>(null);
  
  useFrame(({ clock }) => {
    if (!meshRef.current || !glowRef.current) return;
    
    const time = clock.elapsedTime * 1000;
    
    // Rainbow color cycle
    const hue = ((time + index * 500) / GAME_CONSTANTS.COMET_COLOR_CYCLE_SPEED) % 1;
    const color = new THREE.Color().setHSL(hue, 1.0, 0.5);
    
    // Update material colors
    const material = meshRef.current.material as THREE.MeshStandardMaterial;
    material.color.copy(color);
    material.emissive.copy(color);
    
    const glowMaterial = glowRef.current.material as THREE.MeshBasicMaterial;
    glowMaterial.color.copy(color);
    
    // Pulse effect
    const pulse = 1 + Math.sin(time * 0.005) * 0.1;
    meshRef.current.scale.setScalar(GAME_CONSTANTS.COMET_RADIUS * pulse);
    glowRef.current.scale.setScalar(GAME_CONSTANTS.COMET_RADIUS * pulse * 1.5);
    
    // Update position
    meshRef.current.position.copy(comet.position);
    glowRef.current.position.copy(comet.position);
  });
  
  return (
    <>
      {/* Main comet sphere */}
      <mesh ref={meshRef} position={comet.position.toArray()}>
        <sphereGeometry args={[1, 32, 32]} />
        <meshStandardMaterial
          color="white"
          emissive="white"
          emissiveIntensity={2.0}
        />
      </mesh>
      
      {/* Outer glow */}
      <mesh ref={glowRef} position={comet.position.toArray()}>
        <sphereGeometry args={[1, 16, 16]} />
        <meshBasicMaterial
          color="white"
          transparent
          opacity={0.3}
        />
      </mesh>
    </>
  );
};
